import { useEffect, useRef, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { CONTRACT_DOCUMENT } from '../../data/contractDocument'
import { useContractGameStore } from '../../store/useContractGameStore'

interface DiscoveryControlsProps {
  counterRef?: RefObject<HTMLSpanElement | null>
}

export function DiscoveryControls({ counterRef }: DiscoveryControlsProps) {
  const { t } = useTranslation()
  const discoveredCount = useContractGameStore((s) => s.discoveredClauseIds.length)
  const dispatch = useContractGameStore((s) => s.dispatch)
  const btnRef = useRef<HTMLButtonElement>(null)
  const prevCount = useRef(discoveredCount)

  const totalKeywords = CONTRACT_DOCUMENT.keywordCount
  const progress = totalKeywords > 0 ? (discoveredCount / totalKeywords) * 100 : 0
  const canProceed = discoveredCount > 0

  // Landing pulse when count increases
  useEffect(() => {
    if (discoveredCount > prevCount.current && btnRef.current) {
      btnRef.current.style.animation = 'none'
      // Force reflow
      void btnRef.current.offsetHeight
      btnRef.current.style.animation = 'counter-land 500ms ease-out'
    }
    prevCount.current = discoveredCount
  }, [discoveredCount])

  return (
    <div className="absolute bottom-4 right-4 z-30">
      <button
        ref={btnRef}
        className="discovery-btn relative overflow-hidden py-2 px-5 border border-[#00ff88]/50 rounded-lg text-[#00ff88] text-[14px] font-bold cursor-pointer tracking-wide bg-black/70 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.3)] disabled:opacity-40 disabled:cursor-not-allowed"
        disabled={!canProceed}
        onClick={() => dispatch({ type: 'START_SHOP' })}
        role="progressbar"
        aria-valuenow={discoveredCount}
        aria-valuemin={0}
        aria-valuemax={totalKeywords}
      >
        {/* Progress fill background */}
        <div
          className="absolute inset-0 bg-[#00ff88]/15 transition-[width] duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
        {/* Hover sweep light */}
        <div className="discovery-btn-sweep absolute inset-0 pointer-events-none" />
        {/* Button text */}
        <span className="relative flex items-center gap-2">
          <span ref={counterRef} className="tabular-nums">
            {discoveredCount}/{totalKeywords}
          </span>
          <span>{t('discovery.startResearch')}</span>
        </span>
      </button>
    </div>
  )
}

/** @deprecated Use DiscoveryControls instead */
export const DiscoveryPanel = DiscoveryControls
